import Grid from "@mui/material/Grid";
import Aspera, { HstDataTransfer } from "csi/comm/Aspera";
import Button from "designSystem/components/button/Button";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import MessageDialog from "designSystem/components/dialog/MessageDialog";
import FroalaEditorComponent, { getEditorContent } from "designSystem/components/editor/FroalaEditorComponent";
import File from "designSystem/components/file/File";
import { useLocale } from "designSystem/locale";
import Select from "designSystem/components/select/Select";
import TextField from "designSystem/components/textfield/TextField";
import { UserContext } from "designSystem/user";
import { ATTRIBUTES } from "csi/model/comment";
import { deleteFileData, getAsperaFileData, getFileData } from "csi/service/fileService";
import { stripEmojis } from "csi/service/richText";
import { CommentDetail } from "csi/state/comments";
import { useStore } from "csi/state/hooks";
import { IFileData } from "csi/typings";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { FormEvent, Fragment, FunctionComponent, useCallback, useRef, useState } from "react";
import FroalaEditor from "react-froala-wysiwyg";
import InvalidAttachmentDialog from "./InvalidAttachmentDialog";
import ThreeDPinComponents from "./ThreeDPinComponents";

interface CommentFormProps {
	/** comment node */
	detail?: CommentDetail;
};

export const CommentForm: FunctionComponent<CommentFormProps> = observer(({ detail }) => {
	const isEdit = !!detail;
	const [isValid, setValid] = useState(isEdit);
	const [addedFiles, setAddedFiles] = useState<IFileData[]>([]);
	const [deletedFiles, setDeletedFiles] = useState<IFileData[]>([]);
	const [asperaData, setAsperaData] = useState<HstDataTransfer[]>([]);
	const { commentsStore, rightPaneStore } = useStore();
	const loc = useLocale();
	const { Category, Comment, Subject } = ATTRIBUTES;
	const {
		cancel,
		closeErrorDialog,
		isPinAllowed,
		newCategoryItems,
		newCategory,
		pinEditMode,
		save,
		userProvider,
	} = commentsStore;

	const ref = useRef<FroalaEditor>();
	const saveDisabled = isPinAllowed ? (pinEditMode || !isValid) : !isValid;

	const formRef = useCallback((domNode) => {
		// cleanup added files when unmounted
		if (!domNode) {
			addedFiles.forEach(deleteFileData);
		}
	}, []);

	// push the file to the modal dialog editor
	const pushFilesPreOpenDialog = () => {
		// including the existing ones if any in the case of edit comment case
		const existing = _.difference(detail?.attachments, deletedFiles);

		return [...getFinalFiles(), ...existing];
	};

	// attach the files
	const onAddAttachment = (files: File[], hstData: HstDataTransfer) => {
		if (hstData) {
			setAsperaData([...asperaData, hstData]);
			setAddedFiles([...addedFiles, ...hstData.fileNames.map(getAsperaFileData)]);
		} else {
			setAddedFiles([...addedFiles, ...files.map(getFileData)]);
		}
	};

	// pop up the dialog, when error occurs
	const onAttachError = (errorFileNames) => {
		rightPaneStore.openDialog(InvalidAttachmentDialog, { filenames: errorFileNames });
	};

	// update the deleted ones
	const onDeleteAttachment = (fileData: IFileData) => {
		const remainingFiles = _.difference(addedFiles, [fileData]);
		if (remainingFiles.length != addedFiles.length) {
			setAddedFiles(remainingFiles);
		} else {
			setDeletedFiles([...deletedFiles, fileData]);
		}
	};

	// sync up with the files from the modal dialog editor
	const pullFiles = (_addedFiles: IFileData[], _deletedFiles: IFileData[], hstData?: HstDataTransfer[]) => {
		// when get back from modal dialog, exclude the existing ones if any in the case of edit comment case
		// since they are already part of "files" when pushing
		setAddedFiles(_.difference(_addedFiles, detail?.attachments));
		// Exclude files which were added and deleted while in popup dialog
		// Combine files deleted in dialog with files deleted before we opened the dialog
		setDeletedFiles([..._.filter(_deletedFiles, (file) => _.includes(detail?.attachments, file)), ...deletedFiles]);

		if (hstData?.length) {
			setAsperaData([...asperaData, ...hstData]);
		}
	};

	// get the final files after user uploads and deletes files
	const getFinalFiles = () => _.difference(addedFiles, deletedFiles);

	const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
		const { ThreeDInfo } = ATTRIBUTES;
		const form = event.target as HTMLFormElement;
		event.preventDefault();
		const fd = new FormData(form);
		const titleRes = "CommentSave";

		fd.set(Comment, getEditorContent(ref.current));

		rightPaneStore.openDialog(MessageDialog, {
			category: "progress",
			messageRes: "SavingInProgress",
			titleRes,
		});

		try {
			const finalAddedFiles = getFinalFiles();
			const data = asperaData;
			await save(fd, finalAddedFiles, deletedFiles, data);
			rightPaneStore.closeDialog();
			userProvider.clearSearchCache();
		} catch (error) {
			// use default error message rather than error returned from server
			rightPaneStore.openDialog(ErrorDialog, {
				messageRes: "RequestErrorMessageAndPotentialReasons",
				onClose: closeErrorDialog,
				titleRes,
			});

			console.error(error.message);
		}
	};

	return (
		<form
			autoComplete="off"
			noValidate
			onSubmit={onSubmit}
			ref={formRef}
		>
			<Grid
				container
				direction="column"
				spacing={1}
			>
				{!detail && (
					<Fragment>
						<Grid item>
							<TextField
								autoFocus
								fullWidth
								name={Subject}
								onChange={(event) => setValid(event.target.form.checkValidity() && !!stripEmojis(event.target.value))}
								required
								res={`Comment.Attr.${Subject}`}
							/>
						</Grid>
						<Grid item style={{ width: "100%" }}>
							<Select
								defaultValue={newCategory}
								disabled={newCategoryItems.length === 1}
								fullWidth
								items={newCategoryItems}
								name={Category}
								res={`Comment.Attr.${Category}`}
							/>
						</Grid>
						{isPinAllowed && (
							<Grid item>
								<ThreeDPinComponents />
							</Grid>
						)}
					</Fragment>
				)}
				<Grid
					item
					style={{ marginBottom: 4 }}
					xs
				>
					<UserContext.Provider value={userProvider}>
						<FroalaEditorComponent
							configProps={{
								autofocus: false,
								placeholderText: loc.resource(isEdit ? "CommentEdit" : "CommentContentPlaceholder"),
							}}
							heightMax={isEdit ? 140 : undefined}
							heightMin={isEdit ? 114 : undefined}
							onAttach={onAddAttachment}
							onAttachError={onAttachError}
							pullFiles={pullFiles}
							pushFilesPreOpenDialog={pushFilesPreOpenDialog}
							ref={ref}
							titleRes={isEdit ? "CommentEdit" : "CommentsPlaceholderTitle"}
							value={detail?.content}
						/>
					</UserContext.Provider>
				</Grid>
				<Grid
					container
					direction="column"
					item
					spacing={1}
					wrap="nowrap"
				>
					{addedFiles.map((fileData) =>
						deletedFiles.includes(fileData) ?
							null
							:
							(
								<Grid item key={fileData.id}>
									<File
										fileData={fileData}
										onDelete={!Aspera.isEnabled() ? () => onDeleteAttachment(fileData) : null}
									/>
								</Grid>
							),
					)}
					{detail?.attachments.map((fileData) =>
						deletedFiles.includes(fileData) ? null : (
							<Grid item key={fileData.id}>
								<File
									fileData={fileData}
									onDelete={!Aspera.isEnabled() ? () => onDeleteAttachment(fileData) : null}
								/>
							</Grid>
						),
					)}
				</Grid>
				{isPinAllowed && detail && (
					<Grid item>
						<ThreeDPinComponents />
					</Grid>
				)}
				<Grid
					container
					item
					justifyContent="flex-end"
					spacing={1}
					style={{ marginTop: 4 }}
				>
					<Grid item>
						<Button
							design="primary"
							disabled={saveDisabled}
							res="Save"
							type="submit"
						/>
					</Grid>
					<Grid item>
						<Button
							design="link"
							onClick={() => {
								userProvider.clearSearchCache();
								cancel();
							}}
							res="Cancel"
						/>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
});

export default CommentForm;